<template>
  <div class="pb-wrap">
    <div class="card" style="width:min(900px,100%);">
      <h1 class="h1">Tu tarjeta</h1>
      <p class="p">Vista final (50mm x 90mm). Puedes imprimir o descargar.</p>

      <div class="card-zone">
        <div class="card-preview">
          <img v-if="cardDataUrl" :src="cardDataUrl" alt="Tarjeta" />
          <div v-else class="loading">Generando...</div>
        </div>
      </div>

      <p v-if="saveNote" class="small" style="margin-top: 10px;">{{ saveNote }}</p>

      <div class="row" style="margin-top: 14px;">
        <button class="btn secondary" @click="backToCamera">Volver a cámara</button>
        <button class="btn" :disabled="!cardDataUrl" @click="printCard">Imprimir</button>
        <button class="btn secondary" :disabled="!cardDataUrl" @click="downloadCard">Descargar</button>
        <button class="btn secondary" @click="finish">Terminar</button>
      </div>

      <p class="small" style="margin-top: 10px;">
        Tip: Para que salga exacto, imprime en escala <b>100%</b> (sin ajustar a página).
      </p>

      <canvas ref="cardCanvas" class="hidden"></canvas>
    </div>
  </div>
</template>

<script>
import maskUrl from '@/assets/mascara.png';
import {
  API_GUARDAR_PATH,
  OUTPUT_HEIGHT_PX,
  OUTPUT_WIDTH_PX,
  PRINT_HEIGHT_MM,
  PRINT_WIDTH_MM,
} from '@/config';
import { clearSession, requireFields, setSession } from '@/utils/session';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Dibuja el nombre centrado en el pie, ajustando tamaño si es largo
function drawFooterName(ctx, canvasW, canvasH, rawName) {
  const name = String(rawName || '').trim();
  if (!name) return;

  // Área y márgenes
  const padX = Math.round(canvasW * 0.06);      // 6% a los lados
  const maxTextW = canvasW - padX * 2;
  const y = canvasH - Math.round(canvasH * 0.045); // ~4.5% arriba del borde inferior

  // Tamaño de fuente inicial (responsive)
  let fontSize = Math.round(canvasW * 0.07); // ~43px si w=616
  const minSize = Math.max(18, Math.round(canvasW * 0.045));
  const maxSize = Math.min(72, Math.round(canvasW * 0.09));
  fontSize = Math.max(minSize, Math.min(maxSize, fontSize));

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';

  // Escoge una fuente “segura” sin depender de assets
  const fontFamily = `"Montserrat", "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;

  // Baja tamaño hasta que quepa
  while (fontSize > minSize) {
    ctx.font = `700 ${fontSize}px ${fontFamily}`;
    const w = ctx.measureText(name).width;
    if (w <= maxTextW) break;
    fontSize -= 2;
  }

  // Sombra sutil + contorno para legibilidad
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = Math.round(fontSize * 0.25);
  ctx.shadowOffsetY = Math.round(fontSize * 0.08);

  // Contorno oscuro
  ctx.lineWidth = Math.max(3, Math.round(fontSize * 0.12));
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.strokeText(name, canvasW / 2, y);

  // Relleno blanco
  ctx.shadowBlur = 0; // evita que el relleno se “ensucie”
  ctx.fillStyle = 'rgba(255,255,255,0.98)';
  ctx.fillText(name, canvasW / 2, y);

  ctx.restore();
}

export default {
  name: 'CardView',
  data() {
    return {
      cardDataUrl: '',
      saveNote: '',
    };
  },
  async mounted() {
    const { ok, data } = requireFields(['nombre', 'aceptaTerminos', 'fotoDataUrl']);
    if (!ok) {
      this.$router.replace('/');
      return;
    }
    await this.renderCard(data);
  },
  methods: {
    async renderCard(data) {
      const canvas = this.$refs.cardCanvas;
      if (!canvas) return;

      canvas.width = OUTPUT_WIDTH_PX;
      canvas.height = OUTPUT_HEIGHT_PX;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      try {
        // Base: foto
        const foto = await loadImage(data.fotoDataUrl);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(foto, 0, 0, canvas.width, canvas.height);

        // Encima: máscara
        const mask = await loadImage(maskUrl);
        ctx.drawImage(mask, 0, 0, canvas.width, canvas.height);

        // Encima de TODO: nombre en pie de página
        drawFooterName(ctx, canvas.width, canvas.height, data.nombre);

        // Final
        this.cardDataUrl = canvas.toDataURL('image/png');

        setSession({
          tarjetaDataUrl: this.cardDataUrl,
          tarjetaW: canvas.width,
          tarjetaH: canvas.height,
        });

        // NO bloqueante
        this.postGuardar({
          nombre: data.nombre,
          correo: data.correo || '',
          imagen: this.cardDataUrl,
        });
      } catch (e) {
        console.error('Error renderizando tarjeta:', e);
        this.saveNote =
          'No se pudo cargar la máscara o la imagen. Revisa que exista src/assets/mascara.png';
      }
    },

    async postGuardar(payload) {
      try {
        this.saveNote = 'Registrando datos...';

        await fetch(API_GUARDAR_PATH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        this.saveNote = '';
      } catch (e) {
        console.warn('POST /api/guardar falló (se ignora):', e);
        this.saveNote = '';
      }
    },

    printCard() {
      if (!this.cardDataUrl) return;

      const w = window.open('', '_blank', 'noopener,noreferrer');
      if (!w) {
        alert('El navegador bloqueó la ventana de impresión. Habilita popups para imprimir.');
        return;
      }

      // DOM API: sin strings peligrosos dentro del SFC
      w.document.open();
      w.document.write('<!doctype html><html><head><meta charset="utf-8"><title>Imprimir</title></head><body></body></html>');
      w.document.close();

      const style = w.document.createElement('style');
      style.textContent = `
        @page { size: ${PRINT_WIDTH_MM}mm ${PRINT_HEIGHT_MM}mm; margin: 0; }
        html, body { width: ${PRINT_WIDTH_MM}mm; height: ${PRINT_HEIGHT_MM}mm; margin: 0; padding: 0; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        img { width: ${PRINT_WIDTH_MM}mm; height: ${PRINT_HEIGHT_MM}mm; display: block; }
      `;
      w.document.head.appendChild(style);

      const img = w.document.createElement('img');
      img.src = this.cardDataUrl;
      img.onload = () => setTimeout(() => { try { w.focus(); w.print(); } catch (_) {console.log()} }, 50);

      w.document.body.style.margin = '0';
      w.document.body.style.padding = '0';
      w.document.body.appendChild(img);
    },

    downloadCard() {
      if (!this.cardDataUrl) return;
      const a = document.createElement('a');
      a.href = this.cardDataUrl;
      a.download = 'tarjeta.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },

    backToCamera() {
      this.$router.push('/camera');
    },

    finish() {
      clearSession();
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.card-zone {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.card-preview {
  width: min(420px, 100%);
  aspect-ratio: 5 / 9;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.22);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.card-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
}

.hidden {
  display: none;
}
</style>
